/**
 * Per-site L2 link-layer topology cache.
 *
 * Backed by /api/topology/site/{slug}/linklayer/. Mirrors the
 * topology-mesh store's shape — cache by slug, single in-flight guard,
 * empty fallback on failure. Only fetched in single-site mode where
 * the diagram is actually useful.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";

export interface LinkLayerNode {
  id:           string;
  /** Meraki-minted ID. Same as `id` — kept for callers that care. */
  derivedId:    string;
  serial:       string | null;
  name:         string;
  model:        string;
  /** "device" = Meraki-managed; "discovered" = LLDP/CDP-seen endpoint
   *  (phone, printer, IP camera, unmanaged switch). */
  type:         string;
  /** Meraki's product class: appliance / switch / wireless. Drives the
   *  left-to-right column ordering in the layout. Null for discovered
   *  hosts that don't have a device block. */
  productType:  string | null;
  /** Live status straight from Meraki — online / alerting / offline /
   *  dormant. Replaces the older inventory cross-reference because
   *  Meraki's linkLayer response carries it directly. Null for
   *  discovered hosts. */
  status:       string | null;
  /** Meraki tags exactly one node `root: true` — that's the gateway
   *  the topology hangs off of. The frontend uses this to seed the
   *  BFS instead of guessing by model. */
  isRoot:       boolean;
  ipv4:         string | null;
  mac:          string | null;
  vlan:         number | null;
  lastReported: string | null;
}

export interface LinkLayerLink {
  source:      string;
  target:      string;
  sourcePort:  string | null;
  targetPort:  string | null;
}

export interface LinkLayerTopology {
  nodes: LinkLayerNode[];
  links: LinkLayerLink[];
}

const EMPTY: LinkLayerTopology = { nodes: [], links: [] };

interface State {
  bySlug:  Record<string, LinkLayerTopology>;
  loading: Record<string, true>;
  load: (slug: string) => Promise<LinkLayerTopology>;
}

export const useTopologyLinkLayer = create<State>((set, get) => ({
  bySlug:  {},
  loading: {},

  load: async (slug: string) => {
    const key = slug.toLowerCase();
    const cached = get().bySlug[key];
    if (cached) return cached;
    if (get().loading[key]) {
      // Wait for the in-flight fetch — see topology-mesh store for
      // why we don't dedupe with a Promise; same rationale here.
      return await new Promise<LinkLayerTopology>((resolve) => {
        const t = setInterval(() => {
          const s = get();
          if (s.bySlug[key]) {
            clearInterval(t);
            resolve(s.bySlug[key]);
          }
        }, 80);
      });
    }
    set((s) => ({ loading: { ...s.loading, [key]: true } }));
    try {
      const raw = await apiGet<LinkLayerTopology>(`/topology/site/${encodeURIComponent(key)}/linklayer/`);
      const safe: LinkLayerTopology = {
        nodes: Array.isArray(raw?.nodes) ? raw.nodes : [],
        links: Array.isArray(raw?.links) ? raw.links : [],
      };
      set((s) => ({
        bySlug:  { ...s.bySlug,  [key]: safe },
        loading: { ...s.loading, [key]: undefined as unknown as true },
      }));
      return safe;
    } catch (err) {
      if (!(err instanceof ApiError) || err.status >= 500) {
        set((s) => ({ loading: { ...s.loading, [key]: undefined as unknown as true } }));
      } else {
        set((s) => ({
          bySlug:  { ...s.bySlug,  [key]: EMPTY },
          loading: { ...s.loading, [key]: undefined as unknown as true },
        }));
      }
      return EMPTY;
    }
  },
}));
